import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Label,
  Spinner,
  Badge,
} from "reactstrap";
import { postDataAPI } from "../../helpers/phpbackend_helper";
import { useSelector } from "react-redux";

const MAX_SELECTION = 10;
const PAGE_SIZE = 20;

const isRequestCanceled = (err) => {
  if (err == null) return false;
  const text = typeof err === "string" ? err : err.message || err.code || err.name || "";
  const normalized = String(text).toLowerCase();
  return (
    err.code === "ERR_CANCELED" ||
    err.name === "CanceledError" ||
    err.name === "AbortError" ||
    normalized === "canceled" ||
    normalized === "cancelled" ||
    normalized.includes("canceled") ||
    normalized.includes("cancelled")
  );
};

const getItemPrice = (item) => {
  if (item.price !== undefined && item.price !== null && item.price !== "") {
    return item.price;
  }
  const parts = String(item.cost_combined || "").split("___");
  if (parts[1] !== undefined && parts[1] !== "") {
    const parsed = Number(parts[1]);
    return Number.isNaN(parsed) ? "" : parsed.toFixed(2);
  }
  return "";
};

const BulkCreateFlashcardsModal = ({ isOpen, toggle, onQueued, currency = "" }) => {
  const [items, setItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [courseId, setCourseId] = useState("");
  const [withDescription, setWithDescription] = useState(false);
  const courses = useSelector((state) => state.Dashboard.filterData.courses || []);
  const [hasMore, setHasMore] = useState(true);
  const [loadError, setLoadError] = useState("");

  const controllerRef = useRef(null);
  const fetchLockRef = useRef(false);
  const pageRef = useRef(1);
  const hasMoreRef = useRef(true);
  const searchRef = useRef("");
  const courseRef = useRef("");
  const descriptionRef = useRef(false);
  const listRef = useRef(null);
  const isOpenRef = useRef(false);

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  const fetchItems = useCallback(async (pageToLoad, searchTerm, append, selectedCourse = "", onlyWithDescription = false) => {
    if (!isOpenRef.current) return;
    if (append && fetchLockRef.current) return;
    if (!append && controllerRef.current) {
      controllerRef.current.abort();
    }
    fetchLockRef.current = true;

    const controller = new AbortController();
    controllerRef.current = controller;

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setLoadError("");
    }

    try {
      const res = await postDataAPI(
        {
          serviceName: "item_data",
          op: "server_coaching_outlet_items",
          display_classcification: "No",
          type: "Item",
          status: "Active",
          is_show_all: false,
          load: pageToLoad,
          limit: PAGE_SIZE,
          course_id: selectedCourse || "",
          has_description: onlyWithDescription ? 1 : 0,
          flash_status: "Incomplete",
          exclude_flash_queue: true,
          search: searchTerm,
        },
        controller.signal
      );

      if (!isOpenRef.current || controller.signal.aborted) {
        return;
      }

      if (res.status === "ok") {
        const nextItems = res.data.server_data || [];
        const totalRecords = parseInt(res.data.total_records || 0, 10);
        setItems((prev) => {
          const merged = append ? [...prev, ...nextItems] : nextItems;
          const unique = [];
          const seen = new Set();
          merged.forEach((item) => {
            const id = Number(item.item_id);
            if (!seen.has(id)) {
              seen.add(id);
              unique.push(item);
            }
          });
          const more = unique.length < totalRecords && nextItems.length > 0;
          hasMoreRef.current = more;
          setHasMore(more);
          return unique;
        });
        pageRef.current = pageToLoad;
        setLoadError("");
      } else {
        hasMoreRef.current = false;
        setHasMore(false);
        if (!append) {
          setLoadError(res.message || "Failed to load menu items");
        }
      }
    } catch (err) {
      if (isRequestCanceled(err) || controller.signal.aborted || !isOpenRef.current) {
        return;
      }
      console.error(err);
      hasMoreRef.current = false;
      setHasMore(false);
      if (!append) {
        setLoadError("Failed to load menu items");
      }
    } finally {
      if (controllerRef.current === controller) {
        fetchLockRef.current = false;
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
      fetchLockRef.current = false;
      return;
    }

    setSelectedIds([]);
    setSearchText("");
    setDebouncedSearch("");
    setCourseId("");
    setWithDescription(false);
    setItems([]);
    setLoadError("");
    pageRef.current = 1;
    hasMoreRef.current = true;
    searchRef.current = "";
    courseRef.current = "";
    descriptionRef.current = false;
    setHasMore(true);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    searchRef.current = debouncedSearch;
    courseRef.current = courseId;
    descriptionRef.current = withDescription;
    pageRef.current = 1;
    hasMoreRef.current = true;
    setItems([]);
    setHasMore(true);
    fetchItems(1, debouncedSearch, false, courseId, withDescription);
  }, [isOpen, debouncedSearch, courseId, withDescription, fetchItems]);

  useEffect(() => {
    if (!isOpen || loading || loadingMore || !hasMore || loadError) return;
    const el = listRef.current;
    if (el && el.scrollHeight <= el.clientHeight + 8) {
      fetchItems(pageRef.current + 1, searchRef.current, true, courseRef.current, descriptionRef.current);
    }
  }, [items, isOpen, loading, loadingMore, hasMore, loadError, fetchItems]);

  const handleScroll = (e) => {
    const el = e.target;
    if (!el || loading || loadingMore || !hasMoreRef.current || fetchLockRef.current) {
      return;
    }
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 80;
    if (nearBottom) {
      fetchItems(pageRef.current + 1, searchRef.current, true, courseRef.current, descriptionRef.current);
    }
  };

  const handleSelectItem = (id, checked) => {
    if (checked) {
      setSelectedIds((prev) => {
        if (prev.length >= MAX_SELECTION || prev.includes(id)) return prev;
        return [...prev, id];
      });
      return;
    }
    setSelectedIds((prev) => prev.filter((x) => x !== id));
  };

  const handleCreate = async () => {
    if (selectedIds.length === 0) {
      alert("Please select at least one menu item.");
      return;
    }
    if (selectedIds.length > MAX_SELECTION) {
      alert(`A maximum of ${MAX_SELECTION} menu items can be selected per batch.`);
      return;
    }

    setSubmitting(true);
    try {
      const res = await postDataAPI({
        serviceName: "item_data",
        op: "bulk_queue_flash_cards",
        item_ids: selectedIds,
      });

      if (res.status === "ok") {
        alert(
          res.message ||
            "Flashcards have been queued. Generation will continue in the background."
        );
        if (typeof onQueued === "function") {
          onQueued();
        }
        toggle();
      } else {
        alert(res.message || "Failed to queue flashcards");
      }
    } catch (err) {
      if (isRequestCanceled(err)) return;
      console.error(err);
      alert("Failed to queue flashcards. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const selectionLimitReached = selectedIds.length >= MAX_SELECTION;

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>Create Flashcards in Bulk</ModalHeader>
      <ModalBody>
        <p className="text-muted small mb-3">
          Only menu items without a flashcard are listed. Select up to{" "}
          {MAX_SELECTION} items. Allergens and pictures are not
          generated for bulk flashcards.
        </p>

        <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
          <Input
            placeholder="Search by menu item..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ flex: "1 1 220px" }}
          />
          <Input
            type="select"
            value={courseId}
            onChange={(e) => setCourseId(e.target.value)}
            style={{ flex: "0 1 200px" }}
          >
            <option value="">All courses</option>
            {courses.map((course) => (
              <option key={course.value} value={course.value}>
                {course.label}
              </option>
            ))}
          </Input>
          <div className="form-check mb-0">
            <Input
              type="checkbox"
              id="bulk-items-with-description"
              className="form-check-input"
              checked={withDescription}
              onChange={(e) => setWithDescription(e.target.checked)}
            />
            <Label
              htmlFor="bulk-items-with-description"
              className="form-check-label mb-0"
            >
              Items with description
            </Label>
          </div>
        </div>
        <div className="mb-3">
          <Badge color={selectionLimitReached ? "warning" : "secondary"} pill>
            Selected {selectedIds.length}/{MAX_SELECTION}
          </Badge>
        </div>

        {loadError ? (
          <p className="text-danger text-center mb-0">{loadError}</p>
        ) : loading && items.length === 0 ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <p className="mt-2 text-muted mb-0">Loading menu items...</p>
          </div>
        ) : items.length === 0 ? (
          <p className="text-center text-muted mb-0">
            No menu items without flashcards were found.
          </p>
        ) : (
          <div
            ref={listRef}
            onScroll={handleScroll}
            style={{ maxHeight: "50vh", overflowY: "auto" }}
          >
            {items.map((item) => {
              const itemId = Number(item.item_id);
              const isSelected = selectedIds.includes(itemId);
              const disableUnchecked = selectionLimitReached && !isSelected;
              const price = getItemPrice(item);
              return (
                <div
                  key={itemId}
                  className="form-check mb-2 pb-2 border-bottom"
                >
                  <Input
                    type="checkbox"
                    id={`bulk-flashcard-${itemId}`}
                    checked={isSelected}
                    disabled={disableUnchecked}
                    onChange={(e) =>
                      handleSelectItem(itemId, e.target.checked)
                    }
                    className="form-check-input"
                    style={{ cursor: disableUnchecked ? "not-allowed" : "pointer" }}
                  />
                  <Label
                    htmlFor={`bulk-flashcard-${itemId}`}
                    className="form-check-label"
                    style={{
                      cursor: disableUnchecked ? "not-allowed" : "pointer",
                      opacity: disableUnchecked ? 0.5 : 1,
                    }}
                  >
                    <span className="fw-medium">{item.item_name}</span>
                    <span className="text-muted small d-block">
                      {item.course_name || "Uncategorized"}
                      {price ? ` · ${currency}${price}` : ""}
                    </span>
                  </Label>
                </div>
              );
            })}
            {loadingMore && (
              <div className="text-center py-2">
                <Spinner size="sm" color="primary" />
                <span className="ms-2 text-muted small">Loading more...</span>
              </div>
            )}
            {!hasMore && !loadingMore && (
              <p className="text-center text-muted small mb-0 py-2">
                All matching items loaded
              </p>
            )}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button outline onClick={toggle} disabled={submitting}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleCreate}
          disabled={loading || submitting || selectedIds.length === 0}
        >
          {submitting ? (
            <>
              <Spinner size="sm" className="me-2" />
              Queuing...
            </>
          ) : (
            `Create Flashcards (${selectedIds.length})`
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default BulkCreateFlashcardsModal;
